/**
 * SKU Card Component
 * Displays SKU information in an improved card format
 */

import { useTranslation } from '@ufc/core'
import { Card, CardContent, CardHeader, CardTitle, Badge, cn } from '@ufc/uikit'
import { Package, Layers, Tag } from 'lucide-react'
import type { SKU } from '../../domain/types'
import { getLocalizedName } from '../../domain/utils'
import { PRODUCT_STATUS_CONFIG } from '../../domain/constants'

function getStatusConfig(isActive: boolean) {
  return isActive ? PRODUCT_STATUS_CONFIG.active : PRODUCT_STATUS_CONFIG.inactive
}

interface SKUCardProps {
  sku: SKU
  isSelected?: boolean
  onSelect?: (sku: SKU) => void
  onEdit?: (sku: SKU) => void
  onDelete?: (sku: SKU) => void
  locale?: 'en' | 'ar'
}

export function SKUCard({
  sku,
  isSelected = false,
  onSelect,
  onEdit: _onEdit,
  onDelete: _onDelete,
  locale = 'en',
}: SKUCardProps) {
  const { t } = useTranslation()
  const statusConfig = getStatusConfig(sku.isActive)
  const name = getLocalizedName(sku.name, locale)

  const handleClick = () => {
    onSelect?.(sku)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect?.(sku)
    }
  }

  return (
    <Card
      className={cn(
        'group relative cursor-pointer overflow-hidden border-border/70 bg-gradient-to-b from-card to-muted/20',
        'transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-0.5 hover:shadow-lg',
        isSelected && 'ring-2 ring-primary shadow-lg'
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary/0 via-primary/80 to-primary/0 opacity-0 transition-opacity group-hover:opacity-100" />

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Package className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold truncate">{name}</CardTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t('products.table.variants')}: {sku.variantCount ?? sku.variants?.length ?? 0}
              </p>
            </div>
          </div>

          <Badge
            variant="secondary"
            className={cn('shrink-0 border-0', statusConfig.bgClass, statusConfig.textClass)}
          >
            {t(statusConfig.labelKey)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-col gap-3">
          <div className="relative flex h-36 items-center justify-center rounded-xl border border-border/70 bg-muted/30">
            <Package className="h-10 w-10 text-primary/60" />
          </div>

          {sku.description?.en && (
            <p className="line-clamp-2 text-sm text-muted-foreground">
              {getLocalizedName(sku.description, locale)}
            </p>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-muted/50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Layers className="h-3.5 w-3.5" />
                <span>{t('products.table.variants')}</span>
              </div>
              <p className="mt-1 text-sm font-semibold text-foreground">
                {sku.variantCount ?? sku.variants?.length ?? 0}
              </p>
            </div>

            <div className="rounded-lg bg-muted/50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Tag className="h-3.5 w-3.5" />
                <span>{t('products.table.categories')}</span>
              </div>
              <p className="mt-1 text-sm font-semibold text-foreground">{sku.categories?.length ?? 0}</p>
            </div>
          </div>

          {sku.categories && sku.categories.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {sku.categories.slice(0, 3).map((category) => (
                <Badge key={category.documentId} variant="secondary" className="text-[11px] font-medium">
                  {getLocalizedName(category.name, locale)}
                </Badge>
              ))}
              {sku.categories.length > 3 && (
                <Badge variant="secondary" className="text-[11px] font-medium">
                  +{sku.categories.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
